// src/api.js

const API_BASE = "/api";
const MOCK_MODE = process.env.REACT_APP_MOCK_MODE === 'true';

// Base URL for images served by the backend.
// In production the backend is proxied under /api, so images are at /api/static/...
// In local dev with REACT_APP_MEDIA_URL set (e.g. http://localhost:8000) images
// are fetched directly from the FastAPI server.
export const MEDIA_BASE_URL = process.env.REACT_APP_MEDIA_URL
  ? `${process.env.REACT_APP_MEDIA_URL}/static`
  : "/api/static";

// ─── Portfolio helpers (always hit real API, no mock needed) ────────────────

export async function getPortfolioCategory(category) {
  const res = await fetch(`${API_BASE}/portfolio/${category}`);
  if (!res.ok) throw new Error(`Failed to load portfolio: ${category}`);
  return res.json();
}

export async function createPortfolioProduct(category, data) {
  const res = await fetch(`${API_BASE}/admin/portfolio/${category}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function updatePortfolioProduct(productId, data) {
  const res = await fetch(`${API_BASE}/admin/portfolio/${productId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function deletePortfolioProduct(productId) {
  const res = await fetch(`${API_BASE}/admin/portfolio/${productId}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function uploadPortfolioImage(category, file) {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("category", category);
  const res = await fetch(`${API_BASE}/admin/images/upload?category=${encodeURIComponent(category)}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    body: formData,
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json(); // { path: "/machines-images/..." }
}

export async function deletePortfolioImage(path) {
  const res = await fetch(`${API_BASE}/admin/images?path=${encodeURIComponent(path)}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function listPortfolioImages(category) {
  const res = await fetch(`${API_BASE}/admin/images/${encodeURIComponent(category)}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json(); // { category, images: [...] }
}

export async function getAnalyticsStats(startAt, endAt) {
  const res = await fetch(
    `${API_BASE}/admin/analytics/stats?startAt=${startAt}&endAt=${endAt}`,
    { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
  );
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getAnalyticsPageviews(startAt, endAt, unit = "day") {
  const res = await fetch(
    `${API_BASE}/admin/analytics/pageviews?startAt=${startAt}&endAt=${endAt}&unit=${unit}`,
    { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
  );
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getAnalyticsMetrics(startAt, endAt, type, limit = 10) {
  const res = await fetch(
    `${API_BASE}/admin/analytics/metrics?startAt=${startAt}&endAt=${endAt}&type=${type}&limit=${limit}`,
    { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
  );
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// ─── PLC admin ───────────────────────────────────────────────────────────────

export async function getPLCOverview() {
  const res = await fetch(`${API_BASE}/admin/plc/overview`, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function adminCreateClient(username, password) {
  const res = await fetch(`${API_BASE}/admin/plc/users`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
    body: JSON.stringify({ username, password, role: "client" }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function adminDeleteClient(userId) {
  const res = await fetch(`${API_BASE}/admin/plc/users/${userId}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function adminCreatePLC(data) {
  const res = await fetch(`${API_BASE}/admin/plc/plcs`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function adminUpdatePLC(plcId, data) {
  const res = await fetch(`${API_BASE}/admin/plc/plcs/${plcId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function adminDeletePLC(plcId) {
  const res = await fetch(`${API_BASE}/admin/plc/plcs/${plcId}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function fetchPLCVariables(url) {
  const res = await fetch(`${API_BASE}/admin/plc/fetch-vars?url=${encodeURIComponent(url)}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  if (!res.ok) {
    const j = await res.json().catch(() => ({}));
    throw new Error(j.detail || "Failed to fetch variables");
  }
  return res.json();
}

export async function adminUpdateWritableVars(plcId, vars) {
  const res = await fetch(`${API_BASE}/admin/plc/plcs/${plcId}/writable_vars`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: JSON.stringify(vars),
  });
  if (!res.ok) throw new Error("Failed to update writable variables");
  return res.json();
}

// vars: [{variable_name, display_name, is_boolean, bool_false_label, bool_true_label}]
export async function adminUpdateImportantVars(plcId, vars) {
  const res = await fetch(`${API_BASE}/plc/${plcId}/important_vars`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: JSON.stringify(vars),
  });
  if (!res.ok) throw new Error("Failed to update variables");
  return res.json();
}

// ─── Variable groups ─────────────────────────────────────────────────────────

export async function getVariableGroups(plcId) {
  const res = await fetch(`${API_BASE}/plc/${plcId}/variable_groups`, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  if (!res.ok) throw new Error("Failed to fetch variable groups");
  return res.json();
}

// groups: [{name, variable_names: [...]}]
export async function adminUpdateVariableGroups(plcId, groups) {
  const res = await fetch(`${API_BASE}/admin/plc/plcs/${plcId}/variable_groups`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: JSON.stringify({ groups }),
  });
  if (!res.ok) throw new Error("Failed to update variable groups");
  return res.json();
}

// ─── User session management ─────────────────────────────────────────────────

export async function getAdminUsers() {
  const res = await fetch(`${API_BASE}/admin/users`, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function forceLogoutSession(sessionId) {
  const res = await fetch(`${API_BASE}/admin/users/sessions/${sessionId}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function seedPortfolio() {
  const res = await fetch(`${API_BASE}/admin/portfolio/seed`, {
    method: "POST",
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

const mockData = {
  users: {
    client1: {
      username: "client1",
      role: "client",
      plcs: [
        { id: 1, name: "PLC1", location: "Kanpur" },
      ],
    },
    admin: {
      username: "admin",
      role: "admin",
      plcs: {
        client1: [
          { id: 1, name: "PLC1", location: "Kanpur" },
        ],
        client2: [
          { id: 2, name: "PLC2", location: "Delhi" },
        ],
      },
    },
  },

  latest: {
    1: {
      "AIDI.Dscg_Press_Val": {
        value: 12.4,
        timestamp: "2026-03-14T05:10:00",
      },
      "AIDI.Suct_Temp_Val": {
        value: 18.7,
        timestamp: "2026-03-14T05:10:00",
      },
      "AIDI.Dscg_Temp_Val_1": {
        value: 19.3,
        timestamp: "2026-03-14T05:10:00",
      },
      "AIDI.Coil_Temp_Offs": {
        value: 23,
        timestamp: "2026-03-14T05:10:00",
      },
    },
    2: {
      "AIDI.Dscg_Press_Val": {
        value: 9.8,
        timestamp: "2026-03-14T05:12:00",
      },
      "AIDI.Suct_Temp_Val": {
        value: 16.4,
        timestamp: "2026-03-14T05:12:00",
      },
      "AIDI.Dscg_Temp_Val_1": {
        value: 21.1,
        timestamp: "2026-03-14T05:12:00",
      },
      "AIDI.Coil_Temp_Offs": {
        value: 17,
        timestamp: "2026-03-14T05:12:00",
      },
    },
  },

  importantVars: {
    1: [
      "AIDI.Dscg_Press_Val",
      "AIDI.Suct_Temp_Val",
      "AIDI.Dscg_Temp_Val_1",
      "AIDI.Coil_Temp_Offs",
    ],
    2: [
      "AIDI.Dscg_Press_Val",
      "AIDI.Suct_Temp_Val",
      "AIDI.Dscg_Temp_Val_1",
      "AIDI.Coil_Temp_Offs",
    ],
  },

  writableVars: {
    1: [
      {
        variable_name: "99",
        display_name: "AIDI.Coil_Temp_Offs",
        data_type: "float",
        min_value: -100,
        max_value: 100,
        unit: "°C",
      },
    ],
    2: [
      {
        variable_name: "99",
        display_name: "AIDI.Coil_Temp_Offs",
        data_type: "float",
        min_value: -50,
        max_value: 80,
        unit: "°C",
      },
    ],
  },
};

function generateMockHistory(variable, dateStr) {
  const points = [];
  const baseMap = {
    "AIDI.Dscg_Press_Val": 12,
    "AIDI.Suct_Temp_Val": 19,
    "AIDI.Dscg_Temp_Val_1": 21,
    "AIDI.Coil_Temp_Offs": 23,
  };
  const base = baseMap[variable] ?? 10;

  for (let i = 0; i < 24; i += 1) {
    const hour = String(i).padStart(2, "0");
    points.push({
      timestamp: `${dateStr}T${hour}:00:00`,
      value: Number((base + Math.sin(i / 3) * 1.2 + (i % 5) * 0.15).toFixed(2)),
    });
  }

  return points;
}

function getToken() {
  return localStorage.getItem("token");
}

function getAuthHeaders(extraHeaders = {}) {
  const token = getToken();
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extraHeaders,
  };
}

async function parseError(res) {
  try {
    const data = await res.json();

    if (typeof data.detail === "string") {
      return data.detail;
    }

    if (Array.isArray(data.detail)) {
      return data.detail.map((x) => x.msg || JSON.stringify(x)).join(", ");
    }

    return JSON.stringify(data);
  } catch {
    try {
      return await res.text();
    } catch {
      return "Request failed";
    }
  }
}

async function handleResponse(res) {
  if (!res.ok) {
    const message = await parseError(res);

    if (res.status === 401) {
      localStorage.removeItem("token");
      window.location.href = "/login";
    }

    throw new Error(message || "Request failed");
  }

  const contentType = res.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return res.json();
  }

  return res.text();
}

function delay(ms = 150) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

const mockApi = {
  async get(path) {
    await delay();

    if (path === "/plc") {
      const mockRole = localStorage.getItem("mockRole") || "client";
      const mockUsername = localStorage.getItem("mockUsername") || "client1";
    
      if (mockRole === "admin") {
        return {
          username: mockUsername,
          role: "admin",
          plcs: {
            client1: [
              { id: 1, name: "PLC1", location: "Kanpur" },
              { id: 2, name: "PLC2", location: "Delhi" },
            ],
            client2: [
              { id: 3, name: "PLC3", location: "Jaipur" },
            ],
          },
        };
      }
    
      return {
        username: mockUsername,
        role: "client",
        plcs: [
          { id: 1, name: "PLC1", location: "Kanpur" },
        ],
      };
    }

    const latestMatch = path.match(/^\/plc\/(\d+)\/latest$/);
    if (latestMatch) {
      const plcId = Number(latestMatch[1]);
      return mockData.latest[plcId] || {};
    }

    const importantMatch = path.match(/^\/plc\/(\d+)\/important_vars$/);
    if (importantMatch) {
      const plcId = Number(importantMatch[1]);
      return {
        plc_id: plcId,
        important_vars: mockData.importantVars[plcId] || [],
      };
    }

    const writableMatch = path.match(/^\/plc\/(\d+)\/writable_vars$/);
    if (writableMatch) {
      const plcId = Number(writableMatch[1]);
      return {
        plc_id: plcId,
        writable_vars: mockData.writableVars[plcId] || [],
      };
    }

    const historyMatch = path.match(/^\/plc\/(\d+)\/history\/(.+)\?date=(.+)$/);
    if (historyMatch) {
      const variable = decodeURIComponent(historyMatch[2]);
      const dateStr = historyMatch[3];
      return generateMockHistory(variable, dateStr);
    }

    throw new Error(`No mock GET handler for ${path}`);
  },

  async post(path, data) {
    await delay();

    if (path === "/token") {
      localStorage.setItem("token", "mock-token");
      return {
        access_token: "mock-token",
        token_type: "bearer",
      };
    }

    const controlMatch = path.match(/^\/plc\/(\d+)\/control$/);
    if (controlMatch) {
      return {
        plc_id: Number(controlMatch[1]),
        variable: data.variable,
        value: data.value,
        status: "success",
        message: "Mock value written successfully",
      };
    }

    throw new Error(`No mock POST handler for ${path}`);
  },

  async put(path, data) {
    await delay();
    return { ok: true, path, data };
  },

  async del(path) {
    await delay();
    return { ok: true, path };
  },

  async login(username, password) {
    await delay();
    localStorage.setItem("mockUsername", username);
    localStorage.setItem("mockRole", username === "admin" ? "admin" : "client");
    localStorage.setItem("token", "mock-token");
    return {
      access_token: "mock-token",
      token_type: "bearer",
      username,
      password,
    };
  },

  logout() {
    localStorage.removeItem("token");
    window.location.href = "/login";
  },
};

const realApi = {
  async get(path) {
    const res = await fetch(`${API_BASE}${path}`, {
      method: "GET",
      headers: getAuthHeaders(),
    });
    return handleResponse(res);
  },

  async post(path, data) {
    const res = await fetch(`${API_BASE}${path}`, {
      method: "POST",
      headers: getAuthHeaders({
        "Content-Type": "application/json",
      }),
      body: JSON.stringify(data),
    });
    return handleResponse(res);
  },

  async put(path, data) {
    const res = await fetch(`${API_BASE}${path}`, {
      method: "PUT",
      headers: getAuthHeaders({
        "Content-Type": "application/json",
      }),
      body: JSON.stringify(data),
    });
    return handleResponse(res);
  },

  async del(path) {
    const res = await fetch(`${API_BASE}${path}`, {
      method: "DELETE",
      headers: getAuthHeaders(),
    });
    return handleResponse(res);
  },

  async login(username, password) {
    const res = await fetch(`${API_BASE}/token`, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: new URLSearchParams({ username, password }),
    });

    if (!res.ok) {
      throw new Error("Invalid username or password.");
    }

    const json = await res.json();

    if (json.access_token) {
      localStorage.setItem("token", json.access_token);
    }

    return json;
  },

  logout() {
    localStorage.removeItem("token");
    window.location.href = "/login";
  },
};

const api = MOCK_MODE ? mockApi : realApi;

export default api;